import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize } from 'lucide-react';

interface TaskImage {
  id: number;
  url: string;
  alt?: string;
}

interface TaskImagesGalleryProps {
  images: TaskImage[];
  onDelete?: (imageId: number) => void;
  readOnly?: boolean;
}

const TaskImagesGallery: React.FC<TaskImagesGalleryProps> = ({ 
  images: propImages, 
  onDelete, 
  readOnly = false 
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [displayCount, setDisplayCount] = useState(1);
  
  // Determine how many images to show based on screen width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) { // Mobile
        setDisplayCount(1);
      } else if (width < 1024) { // Tablet
        setDisplayCount(2);
      } else { // Desktop
        setDisplayCount(3);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enhanced debugging for all devices
  useEffect(() => {
    // const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // const isAndroid = /Android/i.test(navigator.userAgent);
    
    if (propImages && propImages.length > 0) {
      // console.log('TaskImagesGallery: Device info', {
      //   isMobile,
      //   isAndroid,
      //   userAgent: navigator.userAgent,
      //   imageCount: propImages.length
      // });
      
      // propImages.forEach((img, index) => {
      //   console.log(`TaskImagesGallery: Image ${index}:`, {
      //     id: img.id,
      //     url: img.url,
      //     alt: img.alt,
      //     isMobile,
      //     isAndroid
      //   });
      // });
    }
  }, [propImages]);

  // Helper to handle closing modal with escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);
  
  if (!propImages || propImages.length === 0) {
    return <div className="p-2 text-gray-500 text-sm">No images available</div>;
  }

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? Math.max(0, propImages.length - displayCount) : Math.max(0, prev - 1)));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev >= propImages.length - displayCount ? prev : prev + 1));
  };

  const openModal = (index: number) => {
    setActiveIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleDelete = (e: React.MouseEvent, imageId: number) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(imageId);
    }
  };

  // Get the visible images based on current activeIndex and displayCount
  const visibleImages = propImages.slice(activeIndex, activeIndex + displayCount);
  
  return (
    <div className="w-full">
      {/* Compact image carousel */}
      <div className="relative bg-gray-100 dark:bg-gray-800 flex overflow-hidden h-[200px] rounded-md w-full">
        {visibleImages.map((image, idx) => (
          <div 
            key={image.id} 
            className="flex-1 h-full p-1"
            style={{ minWidth: `${100/displayCount}%` }}
          >
            <div className="relative h-full w-full overflow-hidden rounded bg-white dark:bg-gray-700">
              <img 
                src={image.url} 
                alt={image.alt || `Image ${activeIndex + idx + 1}`}
                className="h-full w-full object-contain cursor-pointer"
                onClick={() => openModal(activeIndex + idx)}
                onError={(e) => {
                  const isAndroid = /Android/i.test(navigator.userAgent);
                  console.error(`TaskImagesGallery: Failed to load image: ${image.url}`, {
                    isAndroid,
                    userAgent: navigator.userAgent,
                    error: e
                  });
                  
                  // Simplified error handling - same as signatures
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  // Show a fallback message
                  const fallback = document.createElement('div');
                  fallback.className = 'h-full w-full flex items-center justify-center text-gray-500 text-sm bg-gray-100 dark:bg-gray-700';
                  fallback.textContent = 'Image not available';
                  target.parentNode?.appendChild(fallback);
                }}
                onLoad={() => {
                  // Successfully loaded
                  const img = event?.target as HTMLImageElement;
                  if (img) {
                    img.style.opacity = '1';
                  }
                }}
              />
              
              {/* Fullscreen button - smaller and in corner */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  openModal(activeIndex + idx);
                }}
                className="absolute top-1 right-1 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
                aria-label="View fullscreen"
              >
                <Maximize size={14} />
              </button>

              {/* Delete button */}
              {!readOnly && onDelete && (
                <button 
                  onClick={(e) => handleDelete(e, image.id)}
                  className="absolute top-1 hidden left-1 bg-red-500 bg-opacity-70 text-white p-1 rounded-full hover:bg-opacity-100"
                  aria-label="Delete image"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        
        {/* Simplified controls - only show if needed */}
        {propImages.length > displayCount && (
          <>
            <button 
              onClick={handlePrev}
              disabled={activeIndex === 0}
              className={`absolute left-1 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full ${
                activeIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-70'
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <button 
              onClick={handleNext}
              disabled={activeIndex >= propImages.length - displayCount}
              className={`absolute right-1 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full ${
                activeIndex >= propImages.length - displayCount ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-70'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
        
        {/* Small indicator dots for mobile */}
        {propImages.length > 1 && displayCount === 1 && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex space-x-1">
            {propImages.map((_, idx) => (
              <button
                key={idx}
                className={`h-1.5 w-1.5 rounded-full ${
                  idx >= activeIndex && idx < activeIndex + displayCount 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                onClick={() => setActiveIndex(idx)}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Fullscreen modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="relative w-full max-w-4xl h-full max-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeModal}
              className="absolute top-2 right-2 z-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-100"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            
            <img 
              src={propImages[activeIndex].url} 
              alt={propImages[activeIndex].alt || `Task image ${activeIndex + 1}`}
              className="w-full h-full object-contain"
              onError={(e) => {
                const isAndroid = /Android/i.test(navigator.userAgent);
                console.error(`TaskImagesGallery Modal: Failed to load image: ${propImages[activeIndex].url}`, {
                  isAndroid,
                  userAgent: navigator.userAgent,
                  error: e
                });
              }}
            />
            
            {propImages.length > 1 && (
              <>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((prev) => (prev === 0 ? propImages.length - 1 : prev - 1));
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((prev) => (prev === propImages.length - 1 ? 0 : prev + 1));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
                >
                  <ChevronRight size={20} />
                </button>
                
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-sm">
                  {activeIndex + 1} / {propImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskImagesGallery; 